import { Component, OnInit, Input, OnChanges, SimpleChanges, Output, EventEmitter, OnDestroy } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet.vectorgrid';
import { ensurePane } from './leaflet.service';
import { TiledSublayerDescriptor } from './data';
import { HttpClient } from '@angular/common/http';
import { InterpolationService, ScaledStyle, StyleValue } from 'map-wald';
import { Feature } from 'geojson';
import { LeafletMapComponent } from './leaflet-map.component';

const STYLES = {
  fillOpacity: 0.0,
  weight: 1.0
};

const UNSELECTED_FEATURE_STYLES = {
  weight: 1
};

const SELECTED_FEATURE_STYLES= {
  weight:5
};

export enum PointMode {
  default,
  circle
}

@Component({
  selector: 'geojson-layer',
  template: '',
  styles: ['']
})
export class GeojsonLayerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() url: string;
  @Input() features: any;
  // @Input() styles: any;
  @Input() sublayers: TiledSublayerDescriptor[] = [];
  @Input() pointMode:PointMode = PointMode.default;
  @Input() style: ((f:any)=>any) | {[key:string]:StyleValue} = {};
  // @Input() idColumn = 'id';
  @Output() featureSelected = new EventEmitter<any>();
  @Input() zIndex = 100;
  @Input() tooltipFormat: ((f:any)=>string) | string;
  private destroyed = false;
  @Input() selectedFeature: GeoJSON.Feature;
  private selectedFeatureLayer: L.GeoJSON;
  private vectorLayer: L.GeoJSON;
  // private data: any;

  constructor(private http: HttpClient, private map: LeafletMapComponent) {
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if(this.map.map) {
      this.remove(this.map.map);
    }
  }

  private remove(m: L.Map): void {
    if (this.vectorLayer) {
      this.vectorLayer.removeFrom(m);
      this.vectorLayer = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.url) {
      this.downloadLayer(changes.url.currentValue);
    } else if(changes.features||changes.pointMode||changes.style){
      this.makeLayer();
    }
  }

  downloadLayer(url:string): void {
    this.http.get(this.url).subscribe((data: any) => {
      if (url !== this.url) {
        // out of date!
        return;
      }
      this.features = data;

      this.makeLayer();
    });
  }

  makeLayer(): void {
    if(!this.map.map) {
      return;
    }

    const m = this.map.map;
    this.remove(m);
    if (this.destroyed) {
      return;
    }

    const style = (f: any)=>{
      const styles: {[key:string]:any} = {...STYLES};
      if(this.style instanceof Function){
        Object.assign(styles,this.style(f));
      } else {
        Object.keys(this.style).forEach(k=>{
          const val = (this.style as any)[k];
          if((val as ScaledStyle<any>).getStyleValue) {
            styles[k] = (val as ScaledStyle<any>).getStyleValue(f);
          } else {
            styles[k] = val;
          }
        });
      }
      if(f===this.selectedFeature) {
        Object.assign(styles, SELECTED_FEATURE_STYLES);
      }
      return styles;
    };

    const options:L.GeoJSONOptions = {
      // interactive: true
      style: style
    };

    const pane = `vector-pane-${this.zIndex}`;
    ensurePane(m,pane,this.zIndex)
    options.pane = pane;

    if(this.pointMode===PointMode.circle){
      options.pointToLayer = (feature, latlng) => {
        let radius = 3;
        const styleObj = style(feature);
        if(styleObj?.radius) {
          if((styleObj?.radius as ScaledStyle<number>).getStyleValue){
            radius = (styleObj?.radius as ScaledStyle<number>).getStyleValue(feature);
          } else {
            radius = styleObj?.radius as number;
          }
        }
        let layer = L.circleMarker(latlng,{
          radius,
          pane,
          ...style(feature)
        });

        const tooltip = tooltipForFeature(this.tooltipFormat,feature);
        if(tooltip){
          layer = layer.bindTooltip(tooltip);
        }

        return layer;
      };
    }

    this.vectorLayer = L.geoJSON(this.features, options);

    this.vectorLayer.on('click' as any, (event: L.LeafletMouseEvent) => {
      if (this.selectedFeature&&!this.selectedFeatureLayer) {
        this.selectedFeatureLayer = this.vectorLayer.getLayers().find(l=>(l as L.GeoJSON).feature===this.selectedFeature) as L.GeoJSON;
      }

      if(this.selectedFeatureLayer) {
        this.vectorLayer.resetStyle(this.selectedFeatureLayer);
        this.selectedFeatureLayer.setStyle(UNSELECTED_FEATURE_STYLES);
      } // else this.selectedFeature is not in the current layer

      this.selectedFeatureLayer = event.layer;
      this.selectedFeatureLayer.setStyle(SELECTED_FEATURE_STYLES);
      this.selectedFeature = this.selectedFeatureLayer.feature as Feature;
      this.featureSelected.emit(this.selectedFeature);
      event.originalEvent.stopPropagation();
      event.originalEvent.preventDefault();
    });

    this.vectorLayer.addTo(m);
  }
}

export function tooltipForFeature(tooltipFormat: string | ((f: any) => string), feature: Feature): string {
  if(!tooltipFormat){
    return null;
  }

  if(typeof(tooltipFormat)==='function'){
    return tooltipFormat(feature);
  }

  return InterpolationService.interpolate(tooltipFormat,feature.properties,{
    decimalPlaces:2,
    recursive:true
  });
}


